import { useEffect, useRef, useState } from "react";
import HeroNav from "./HeroNav";

const TILE = 130; // unified tile size for both left and right magazine walls
const WORDMARK = ["F", "o", "l", "i", "o", "m"];

type Tile = {
  col: number;
  row: number;
  src: string;
  alt: string;
};

// Right-side magazine wall — 11 unique covers, mirrored staircase
// densest at bottom-right, thinning toward upper-left
const rightTiles: Tile[] = [
  // ---------- Row 4 (bottom): 4 tiles ----------
  {
    col: 1,
    row: 4,
    src: "https://readdy.ai/api/search-image?query=Editorial%20magazine%20cover%20still%20life%20of%20vintage%20leather%20bound%20books%20stacked%20on%20oak%20table%20with%20brass%20reading%20lamp%2C%20deep%20burgundy%20and%20amber%20tones%2C%20moody%20cinematic%20studio%20lighting%2C%20refined%20painterly%20fine%20art%20photography%2C%20high%20detail%20texture%2C%20literary%20publication%20aesthetic&width=520&height=520&seq=mag-wall-r-01&orientation=squarish",
    alt: "The Reading Room cover",
  },
  {
    col: 2,
    row: 4,
    src: "https://readdy.ai/api/search-image?query=Editorial%20fashion%20magazine%20cover%20portrait%20of%20elegant%20woman%20with%20short%20bob%20hair%20wearing%20pearl%20drop%20earrings%20and%20cream%20silk%20blouse%2C%20moody%20chiaroscuro%20lighting%2C%20deep%20forest%20green%20backdrop%2C%20rich%20painterly%20portraiture%2C%20vintage%20couture%20publication%20aesthetic%2C%20fine%20art%20photography&width=520&height=520&seq=mag-wall-r-02&orientation=squarish",
    alt: "Atelier portrait cover",
  },
  {
    col: 3,
    row: 4,
    src: "https://readdy.ai/api/search-image?query=Editorial%20travel%20magazine%20cover%20of%20windswept%20desert%20dunes%20at%20golden%20hour%20with%20lone%20traveler%20silhouette%2C%20warm%20sand%20amber%20and%20copper%20tones%2C%20painterly%20cinematic%20landscape%2C%20refined%20fine%20art%20photography%2C%20rich%20texture%2C%20wanderlust%20publication%20aesthetic&width=520&height=520&seq=mag-wall-r-03&orientation=squarish",
    alt: "Sahara journey cover",
  },
  {
    col: 4,
    row: 4,
    src: "https://readdy.ai/api/search-image?query=Editorial%20design%20magazine%20cover%20of%20mid%20century%20modern%20lounge%20chair%20in%20cognac%20leather%20beside%20brass%20floor%20lamp%2C%20warm%20cream%20wall%20backdrop%2C%20soft%20directional%20daylight%2C%20refined%20painterly%20still%20life%2C%20high%20detail%20wood%20grain%20texture%2C%20interior%20publication%20aesthetic&width=520&height=520&seq=mag-wall-r-04&orientation=squarish",
    alt: "Mid-century interior cover",
  },

  // ---------- Row 3: 4 tiles ----------
  {
    col: 1,
    row: 3,
    src: "https://readdy.ai/api/search-image?query=Editorial%20food%20magazine%20cover%20still%20life%20of%20freshly%20baked%20rustic%20sourdough%20bread%20with%20linen%20cloth%20and%20copper%20bowl%20of%20figs%2C%20deep%20burgundy%20and%20wheat%20tones%2C%20moody%20cinematic%20studio%20lighting%2C%20painterly%20fine%20art%20photography%2C%20high%20detail%20texture%2C%20culinary%20publication%20aesthetic&width=520&height=520&seq=mag-wall-r-05&orientation=squarish",
    alt: "Slow Kitchen cover",
  },
  {
    col: 2,
    row: 3,
    src: "https://readdy.ai/api/search-image?query=Editorial%20fashion%20magazine%20cover%20portrait%20of%20stylish%20man%20wearing%20straw%20boater%20hat%20and%20cat%20eye%20white%20sunglasses%20with%20denim%20jacket%20over%20cream%20shirt%2C%20moody%20cinematic%20studio%20lighting%2C%20warm%20amber%20and%20deep%20navy%20tones%2C%20refined%20painterly%20fine%20art%20portraiture%2C%20high%20detail%20texture%2C%20publication%20aesthetic&width=520&height=520&seq=mag-wall-r-06&orientation=squarish",
    alt: "Summer Edit cover",
  },
  {
    col: 3,
    row: 3,
    src: "https://readdy.ai/api/search-image?query=Editorial%20music%20magazine%20cover%20still%20life%20of%20black%20vinyl%20record%20on%20vintage%20turntable%20with%20brass%20tonearm%2C%20deep%20charcoal%20and%20copper%20tones%2C%20moody%20cinematic%20studio%20lighting%2C%20painterly%20fine%20art%20photography%2C%20rich%20texture%20detail%2C%20analog%20publication%20aesthetic&width=520&height=520&seq=mag-wall-r-07&orientation=squarish",
    alt: "Long Play cover",
  },
  {
    col: 4,
    row: 3,
    src: "https://readdy.ai/api/search-image?query=Editorial%20art%20magazine%20cover%20of%20abstract%20expressionist%20oil%20painting%20with%20bold%20ochre%20and%20deep%20rust%20brushstrokes%20on%20cream%20canvas%2C%20moody%20gallery%20lighting%2C%20painterly%20rich%20texture%2C%20refined%20fine%20art%20publication%20aesthetic%2C%20museum%20catalogue%20composition&width=520&height=520&seq=mag-wall-r-08&orientation=squarish",
    alt: "Modern Canvas cover",
  },

  // ---------- Row 2: 2 tiles right side ----------
  {
    col: 3,
    row: 2,
    src: "https://readdy.ai/api/search-image?query=Editorial%20cinema%20magazine%20cover%20portrait%20of%20mysterious%20woman%20in%20film%20noir%20style%20with%20wide%20brim%20hat%20casting%20dramatic%20shadow%20across%20face%2C%20black%20and%20warm%20sepia%20tones%2C%20smoky%20chiaroscuro%20lighting%2C%20painterly%20vintage%20portraiture%2C%20classic%20cinema%20publication%20aesthetic&width=520&height=520&seq=mag-wall-r-09&orientation=squarish",
    alt: "Noir Hour cover",
  },
  {
    col: 4,
    row: 2,
    src: "https://readdy.ai/api/search-image?query=Editorial%20architecture%20magazine%20cover%20of%20brutalist%20concrete%20tower%20at%20golden%20hour%20with%20dramatic%20shadow%20play%2C%20warm%20stone%20cream%20and%20deep%20charcoal%20tones%2C%20painterly%20cinematic%20composition%2C%20refined%20fine%20art%20photography%2C%20high%20detail%20texture%2C%20design%20publication%20aesthetic&width=520&height=520&seq=mag-wall-r-10&orientation=squarish",
    alt: "Concrete Form cover",
  },

  // ---------- Row 1 (top): 1 tile far right ----------
  {
    col: 4,
    row: 1,
    src: "https://readdy.ai/api/search-image?query=Editorial%20nature%20magazine%20cover%20of%20lush%20botanical%20monstera%20and%20fern%20leaves%20arranged%20against%20deep%20forest%20green%20backdrop%2C%20soft%20directional%20daylight%2C%20painterly%20fine%20art%20still%20life%20photography%2C%20rich%20texture%20detail%2C%20botanical%20publication%20aesthetic%2C%20refined%20composition&width=520&height=520&seq=mag-wall-r-11&orientation=squarish",
    alt: "Green Hours cover",
  },
];

// Left-side smaller stack — 6 unique covers, mirrored toward bottom-left
// densest at bottom-left, thinning toward upper-right
const leftTiles: Tile[] = [
  // Bottom row: 3 tiles
  {
    col: 1,
    row: 3,
    src: "https://readdy.ai/api/search-image?query=Editorial%20coffee%20magazine%20cover%20still%20life%20of%20ceramic%20espresso%20cup%20on%20rustic%20oak%20table%20with%20linen%20napkin%20and%20whole%20coffee%20beans%2C%20warm%20cream%20and%20deep%20espresso%20brown%20tones%2C%20soft%20morning%20light%2C%20painterly%20fine%20art%20still%20life%20photography%2C%20cafe%20publication%20aesthetic&width=520&height=520&seq=mag-wall-l-01&orientation=squarish",
    alt: "Slow Brew cover",
  },
  {
    col: 2,
    row: 3,
    src: "https://readdy.ai/api/search-image?query=Editorial%20portrait%20magazine%20cover%20of%20elegant%20woman%20with%20vintage%20pearl%20earring%20and%20cream%20silk%20headscarf%2C%20deep%20burgundy%20painted%20backdrop%2C%20moody%20chiaroscuro%20studio%20lighting%2C%20painterly%20fine%20art%20portraiture%20inspired%20by%20old%20masters%2C%20refined%20couture%20publication%20aesthetic&width=520&height=520&seq=mag-wall-l-02&orientation=squarish",
    alt: "Atelier portrait cover",
  },
  {
    col: 3,
    row: 3,
    src: "https://readdy.ai/api/search-image?query=Editorial%20travel%20magazine%20cover%20of%20misty%20alpine%20mountains%20at%20dawn%20with%20lone%20hiker%20silhouette%20on%20ridge%2C%20cool%20slate%20blue%20and%20warm%20peach%20sunrise%20tones%2C%20painterly%20cinematic%20landscape%20photography%2C%20fine%20art%20detail%2C%20expedition%20publication%20aesthetic&width=520&height=520&seq=mag-wall-l-03&orientation=squarish",
    alt: "Highlands journal cover",
  },

  // Middle row: 2 tiles
  {
    col: 1,
    row: 2,
    src: "https://readdy.ai/api/search-image?query=Editorial%20jazz%20magazine%20cover%20portrait%20of%20saxophone%20player%20mid%20performance%20in%20smoky%20dimly%20lit%20lounge%2C%20deep%20amber%20and%20charcoal%20tones%2C%20moody%20cinematic%20stage%20lighting%2C%20painterly%20fine%20art%20portraiture%2C%20rich%20texture%20detail%2C%20classic%20music%20publication%20aesthetic&width=520&height=520&seq=mag-wall-l-04&orientation=squarish",
    alt: "Blue Note cover",
  },
  {
    col: 2,
    row: 2,
    src: "https://readdy.ai/api/search-image?query=Editorial%20design%20magazine%20cover%20of%20handcrafted%20ceramic%20vase%20with%20single%20dried%20pampas%20grass%20stem%20on%20cream%20linen%20surface%2C%20warm%20bone%20and%20clay%20earth%20tones%2C%20soft%20directional%20morning%20daylight%2C%20painterly%20minimalist%20still%20life%2C%20refined%20craft%20publication%20aesthetic&width=520&height=520&seq=mag-wall-l-05&orientation=squarish",
    alt: "Hand & Form cover",
  },

  // Top: 1 tile
  {
    col: 1,
    row: 1,
    src: "https://readdy.ai/api/search-image?query=Editorial%20dance%20magazine%20cover%20portrait%20of%20modern%20ballet%20dancer%20mid%20movement%20in%20flowing%20cream%20silk%20costume%2C%20deep%20charcoal%20studio%20backdrop%2C%20moody%20cinematic%20stage%20lighting%2C%20painterly%20fine%20art%20portraiture%2C%20refined%20motion%20capture%2C%20performing%20arts%20publication%20aesthetic&width=520&height=520&seq=mag-wall-l-06&orientation=squarish",
    alt: "In Motion cover",
  },
];

export default function Hero() {
  const [scrollY, setScrollY] = useState(0);
  const [viewport, setViewport] = useState({
    w: typeof window !== "undefined" ? window.innerWidth : 1440,
    h: typeof window !== "undefined" ? window.innerHeight : 900,
  });

  // Measure the un-rotated wordmark size so the rotation pivot
  // (visible top-right corner of the vertical text) stays glued
  // to a single screen point throughout the rotation.
  const wordmarkRef = useRef<HTMLHeadingElement>(null);
  const [textDims, setTextDims] = useState({ w: 620, h: 224 });

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    const onResize = () =>
      setViewport({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    onScroll();
    onResize();
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  useEffect(() => {
    const measure = () => {
      if (wordmarkRef.current) {
        setTextDims({
          w: wordmarkRef.current.offsetWidth,
          h: wordmarkRef.current.offsetHeight,
        });
      }
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [viewport.w]);

  // Parallax lift: the wordmark drifts upward as the user scrolls
  const maxLift = TILE * 2.2;
  const lift = Math.min(scrollY * 0.75, maxLift);

  // Wall rearrangement progress (0 → 1) over the first ~600px of scroll.
  // Tiles slide upward while the staircase flips vertically into its mirrored form.
  const wallProgress = Math.min(Math.max(scrollY / 600, 0), 1);
  const easeInOut = (x: number) => (x < 0.5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2);
  const wallEase = easeInOut(wallProgress);

  // Extra collective upward drift for both walls during the rearrangement
  const wallDrift = wallEase * TILE * 0.4;

  // Dock phase: once the wordmark has fully lifted, it rotates from vertical to
  // horizontal and slides into the top-right corner, then locks there.
  const dockProgress = Math.min(Math.max((scrollY - 500) / 420, 0), 1);
  const dockEase = easeInOut(dockProgress);

  // Fixed font size — the wordmark must NOT change size during rotation.
  const wordmarkFontSize = Math.round(TILE * 1.4);

  // The CSS pivot is the top-left of the un-rotated text box. After a 90° CW
  // rotation, that same point becomes the visible TOP-RIGHT corner of the
  // vertical text — i.e. exactly where the yellow marker sits in the reference.
  // So we just position the h1's (left, top) at the pivot, set
  // transform-origin: top left, and the pivot stays glued during rotation.

  // Initial pivot (vertical state, scrollY = 0):
  // place the vertical wordmark between the two magazine walls, F at the top.
  const verticalColumnCenterX =
    TILE * 3 + (viewport.w - TILE * 7) / 2;
  const originPivotX = verticalColumnCenterX + textDims.h / 2;
  const originPivotY = viewport.h - TILE * 2 - textDims.w / 2 - lift;

  // Docked pivot (horizontal state):
  // top-left of horizontal text — positioned so the text reaches ~30px from
  // the right edge of the viewport and sits near the top.
  const dockPivotX = viewport.w - textDims.w - 30;
  const dockPivotY = 60;

  const wordmarkPivotX =
    originPivotX + (dockPivotX - originPivotX) * dockEase;
  const wordmarkPivotY =
    originPivotY + (dockPivotY - originPivotY) * dockEase;

  // Rotation interpolates from 90deg (vertical) to 0deg (horizontal)
  const wordmarkRotation = 90 * (1 - dockEase);

  return (
    <section className="relative w-full bg-black" style={{ height: "280vh" }}>
      <div className="sticky top-0 w-full h-screen overflow-hidden flex flex-col">
      {/* Subtle radial vignette behind logo */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 50% 28%, rgba(255,255,255,0.07) 0%, rgba(0,0,0,0) 55%)",
        }}
      ></div>

      <HeroNav />

      {/* spacer so the section keeps its tall hero feel */}
      <div className="relative z-10 flex-1" style={{ minHeight: `${TILE * 4}px` }}></div>

      {/* Giant wordmark — starts vertical between the two magazine walls, then
          rotates around its visible top-right corner (the yellow-dot pivot)
          into the top-right docked slot. Font size stays fixed throughout. */}
      <h1
        ref={wordmarkRef}
        className="hidden md:block absolute z-[6] font-serif text-white leading-none tracking-tight whitespace-nowrap pointer-events-none select-none will-change-transform"
        style={{
          left: `${wordmarkPivotX}px`,
          top: `${wordmarkPivotY}px`,
          fontSize: `${wordmarkFontSize}px`,
          transform: `rotate(${wordmarkRotation}deg)`,
          transformOrigin: "top left",
        }}
      >
        {WORDMARK.map((ch, idx) => (
          <span key={`${ch}-${idx}`} className="inline-block">
            {ch}
          </span>
        ))}
      </h1>

      {/* RIGHT magazine wall — starts dense at bottom-right, slides up & flips vertically as you scroll */}
      <div
        className="hidden md:block absolute bottom-0 right-0 z-[5] pointer-events-none select-none will-change-transform"
        style={{
          width: `${TILE * 4}px`,
          height: `${TILE * 4}px`,
          transform: `translateY(${-wallDrift}px)`,
        }}
      >
        {rightTiles.map((t, i) => {
          const startTop = (t.row - 1) * TILE;
          const endRow = 5 - t.row; // vertical mirror across the 4-row grid
          const endTop = (endRow - 1) * TILE;
          const currentTop = startTop + (endTop - startTop) * wallEase;
          return (
            <div
              key={`r-${i}`}
              className="absolute overflow-hidden ring-1 ring-white/5 will-change-transform"
              style={{
                width: `${TILE}px`,
                height: `${TILE}px`,
                right: `${(4 - t.col) * TILE}px`,
                top: `${currentTop}px`,
              }}
            >
              <img
                src={t.src}
                alt={t.alt}
                className="w-full h-full object-cover object-top"
              />
            </div>
          );
        })}

        {/* Label "POWERED" — between row 1 and row 2, top-right area */}
        <div
          className="absolute text-[10px] tracking-[0.25em] uppercase font-mono"
          style={{
            right: "0px",
            top: `${TILE - 18}px`,
            color: "#e94e1b",
          }}
        >
          Powered
        </div>

        {/* Label "PASSION" — between row 2 and row 3, mid-left area of stack */}
        <div
          className="absolute text-[10px] tracking-[0.25em] uppercase font-mono"
          style={{
            right: `${TILE * 2}px`,
            top: `${TILE * 2 - 18}px`,
            color: "#e94e1b",
          }}
        >
          Passion
        </div>
      </div>

      {/* LEFT magazine wall — same tile size, mirrors the right wall's vertical flip as you scroll */}
      <div
        className="hidden lg:block absolute bottom-0 left-0 z-[5] pointer-events-none select-none will-change-transform"
        style={{
          width: `${TILE * 3}px`,
          height: `${TILE * 3}px`,
          transform: `translateY(${-wallDrift}px)`,
        }}
      >
        {leftTiles.map((t, i) => {
          const startTop = (t.row - 1) * TILE;
          const endRow = 4 - t.row; // vertical mirror across the 3-row grid
          const endTop = (endRow - 1) * TILE;
          const currentTop = startTop + (endTop - startTop) * wallEase;
          return (
            <div
              key={`l-${i}`}
              className="absolute overflow-hidden ring-1 ring-white/5 will-change-transform"
              style={{
                width: `${TILE}px`,
                height: `${TILE}px`,
                left: `${(t.col - 1) * TILE}px`,
                top: `${currentTop}px`,
              }}
            >
              <img
                src={t.src}
                alt={t.alt}
                className="w-full h-full object-cover object-top"
              />
            </div>
          );
        })}

        {/* Label "EDITED" — mirror of POWERED, top-left area */}
        <div
          className="absolute text-[10px] tracking-[0.25em] uppercase font-mono"
          style={{
            left: "0px",
            top: `${TILE - 18}px`,
            color: "#e94e1b",
          }}
        >
          Edited
        </div>

        {/* Label "STORIES" — mirror of PASSION */}
        <div
          className="absolute text-[10px] tracking-[0.25em] uppercase font-mono"
          style={{
            left: `${TILE * 2 - 56}px`,
            top: `${TILE * 2 - 18}px`,
            color: "#e94e1b",
          }}
        >
          Stories
        </div>
      </div>

      {/* Micro caption — sits above the left stack */}
      <div
        className="hidden lg:block absolute z-[6] max-w-[260px]"
        style={{ left: `${TILE - 26}px`, bottom: `${TILE * 3 + 70}px` }}
      >
        <p className="text-[10px] tracking-[0.3em] uppercase text-white/55 font-mono mb-3">
          Small Pages. Big Worlds.
        </p>
        <p className="text-white/85 text-sm leading-relaxed">
          From the first chapter to the final margin — editions worth keeping
          on the shelf.
        </p>
      </div>
      </div>
    </section>
  );
}